/**
 * Resume State Store
 * Manages the resume data in a structured format that maps to RenderCV YAML
 */

import yaml from 'js-yaml';

export const RESUME_STORAGE_KEY = 'analyzed_resume_data';
export const JD_STORAGE_KEY = 'analyzed_job_description';
export const ANALYSIS_RESULT_KEY = 'latest_analysis_result';


// All social networks supported by RenderCV
export const SUPPORTED_NETWORKS = [
  'LinkedIn',
  'GitHub',
  'GitLab',
  'X',
  'YouTube',
  'Leetcode',
  'StackOverflow',
  'ORCID',
  'ResearchGate',
  'GoogleScholar',
  'Instagram',
  'Mastodon',
  'Telegram',
  'WhatsApp',
  'Bluesky',
  'IMDB'
] as const;

export type SupportedNetwork = typeof SUPPORTED_NETWORKS[number];

export interface SocialNetwork {
  network: SupportedNetwork;
  username: string;
}


export interface ExperienceEntry {
  id: string;
  company: string;
  position: string;
  location: string;
  start_date: string;
  end_date: string;
  highlights: string[];
}

export interface EducationEntry {
  id: string;
  institution: string;
  area: string;
  degree: string;
  start_date: string;
  end_date: string;
  location: string;
  gpa?: string;
  highlights: string[];
}

// Additional RenderCV entry types
export interface NormalEntry {
  id: string;
  name: string;
  location?: string;
  date?: string;
  highlights?: string[];
}

export interface PublicationEntry {
  id: string;
  title: string;
  authors: string[];
  date?: string;
  doi?: string;
  journal?: string;
  url?: string;
}

export interface OneLineEntry {
  id: string;
  label: string;
  details: string;
}

// BulletEntry, NumberedEntry, ReversedNumberedEntry are just string arrays
// TextEntry is also just a string

// Union type for all supported entry types
export type AnyEntry = ExperienceEntry | EducationEntry | NormalEntry | PublicationEntry | OneLineEntry | string;

// Entry type identifiers for UI
export const ENTRY_TYPES = [
  { id: 'text', name: 'Text', description: 'Simple text items (bullets)' },
  { id: 'experience', name: 'Experience', description: 'Work experience with company, role, dates' },
  { id: 'education', name: 'Education', description: 'Education with institution, degree, dates' },
  { id: 'normal', name: 'Normal', description: 'Generic entry with name, location, date' },
  { id: 'publication', name: 'Publication', description: 'Academic publication with authors, journal' },
  { id: 'oneline', name: 'One-Line', description: 'Label and details on one line' }
] as const;

export type EntryTypeId = typeof ENTRY_TYPES[number]['id'];


export interface ResumeData {
  cv: {
    name: string;
    email: string;
    phone: string;
    location: string;
    website: string;
    social_networks: SocialNetwork[];
    sections: {
      Summary?: string[];
      Experience?: ExperienceEntry[];
      Education?: EducationEntry[];
      Skills?: string[];
      Projects?: ExperienceEntry[];
      Publications?: PublicationEntry[];
      "Extracurricular Activities"?: ExperienceEntry[];
      // Custom sections with their entry type
      [key: string]: AnyEntry[] | undefined;
    };
    // Order of sections for PDF generation and UI tabs
    section_order?: string[];
    // Metadata to track entry type for custom sections (not sent to API)
    _customSectionTypes?: Record<string, EntryTypeId>;
  };
  design: DesignSettings;
  locale?: LocaleSettings;
  rendercv_settings?: RenderCVSettings;
}

// ============================================
// COMPLETE RenderCV Design Settings Schema
// (Matches actual RenderCV CLI output)
// ============================================

export interface DesignSettings {
  theme: 'classic' | 'sb2nov' | 'moderncv' | 'engineeringresumes';
  page?: PageSettings;
  colors?: ColorSettings;
  typography?: TypographySettings;
  links?: LinkSettings;
  header?: HeaderSettings;
  section_titles?: SectionTitleSettings;
  sections?: SectionsSettings;
  entries?: EntrySettings;
}

// --- Page Settings ---
export interface PageSettings {
  size?: 'us-letter' | 'a4';
  top_margin?: string;
  bottom_margin?: string;
  left_margin?: string;
  right_margin?: string;
  show_footer?: boolean;
  show_top_note?: boolean;
}

// --- Color Settings (matches RenderCV exactly) ---
export interface ColorSettings {
  body?: string;
  name?: string;
  headline?: string;
  connections?: string;
  section_titles?: string;
  links?: string;
  footer?: string;
  top_note?: string;
}

// --- Typography Settings (matches RenderCV exactly) ---
export interface TypographySettings {
  line_spacing?: string;
  alignment?: 'justified' | 'left';
  date_and_location_column_alignment?: 'left' | 'right';
  font_family?: {
    body?: string;
    name?: string;
    headline?: string;
    connections?: string;
    section_titles?: string;
  };
  font_size?: {
    body?: string;
    name?: string;
    headline?: string;
    connections?: string;
    section_titles?: string;
  };
  small_caps?: {
    name?: boolean;
    headline?: boolean;
    connections?: boolean;
    section_titles?: boolean;
  };
  bold?: {
    name?: boolean;
    headline?: boolean;
    connections?: boolean;
    section_titles?: boolean;
  };
}

// --- Link Settings ---
export interface LinkSettings {
  underline?: boolean;
  show_external_link_icon?: boolean;
}

// --- Header Settings ---
export interface HeaderSettings {
  alignment?: 'left' | 'center' | 'right';
  photo_width?: string;
  photo_position?: 'left' | 'right';
  space_below_name?: string;
  space_below_headline?: string;
  space_below_connections?: string;
  connections?: {
    phone_number_format?: 'national' | 'international';
    hyperlink?: boolean;
    show_icons?: boolean;
    separator?: string;
    space_between_connections?: string;
  };
}

// --- Section Title Settings ---
export interface SectionTitleSettings {
  type?: 'with_partial_line' | 'with_full_line' | 'no_line';
  line_thickness?: string;
  space_above?: string;
  space_below?: string;
}

// --- Sections Settings ---
export interface SectionsSettings {
  allow_page_break?: boolean;
  space_between_regular_entries?: string;
  space_between_text_based_entries?: string;
}

// --- Entry Settings ---
export interface EntrySettings {
  date_and_location_width?: string;
  side_space?: string;
  space_between_columns?: string;
  allow_page_break?: boolean;
  short_second_row?: boolean;
  highlights?: {
    bullet?: string;
    nested_bullet?: string;
    space_left?: string;
    space_above?: string;
    space_between_items?: string;
  };
}

// --- Entry Type Templates ---
export interface EntryTypeSettings {
  one_line_entry?: {
    template?: string;
  };
  education_entry?: {
    main_column_first_row_template?: string;
    degree_column_template?: string;
    degree_column_width?: string;
    main_column_second_row_template?: string;
    date_and_location_column_template?: string;
  };
  normal_entry?: {
    main_column_first_row_template?: string;
    main_column_second_row_template?: string;
    date_and_location_column_template?: string;
  };
  experience_entry?: {
    main_column_first_row_template?: string;
    main_column_second_row_template?: string;
    date_and_location_column_template?: string;
  };
  publication_entry?: {
    main_column_first_row_template?: string;
    main_column_second_row_template?: string;
    main_column_second_row_without_journal_template?: string;
    main_column_second_row_without_url_template?: string;
    date_and_location_column_template?: string;
  };
}

// --- Locale Settings ---
export interface LocaleSettings {
  language?: string;
  phone_number_format?: 'national' | 'international' | 'e164';
  page_numbering_template?: string;
  last_updated_date_template?: string;
  date_template?: string;
  month?: string;
  months?: string;
  year?: string;
  years?: string;
  present?: string;
  to?: string;
  abbreviations_for_months?: string[];
  full_names_of_months?: string[];
}

// --- RenderCV Settings ---
export interface RenderCVSettings {
  date?: string;
  bold_keywords?: string[];
  sort_entries?: 'none' | 'ascending' | 'descending';
}

// Default empty resume
export const defaultResumeData: ResumeData = {
  cv: {
    name: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    social_networks: [],
    sections: {
      Summary: [''],
      Experience: [],
      Education: [],
      Skills: [],
      Projects: [],
      Publications: [],
      "Extracurricular Activities": []
    },
    section_order: [
      'Summary',
      'Experience',
      'Projects',
      'Education',
      'Skills',
      'Publications',
      'Extracurricular Activities'
    ]
  },
  design: {
    theme: 'classic'
  }
};

// Sample resume template with example data (RenderCV compatible)
export const sampleResumeData: ResumeData = {
  cv: {
    name: 'John Doe',
    email: 'john.doe@example.com',
    phone: '', // Phone removed due to RenderCV strict validation
    location: 'San Francisco, CA, USA',
    website: 'https://johndoe.dev',
    social_networks: [
      { network: 'LinkedIn', username: 'johndoe' },
      { network: 'GitHub', username: 'johndoe' }
    ],
    sections: {
      Summary: [
        'Full-stack software engineer with 5+ years of experience building scalable web applications. Passionate about clean code, user experience, and continuous learning.'
      ],
      Education: [
        {
          id: 'edu1',
          institution: 'Stanford University',
          area: 'Computer Science',
          degree: 'MS',
          start_date: '2018-09',
          end_date: '2020-06',
          location: 'Stanford, CA',
          highlights: ['GPA: 3.9/4.0', 'Teaching Assistant for CS101']
        },
        {
          id: 'edu2',
          institution: 'UC Berkeley',
          area: 'Computer Science',
          degree: 'BS',
          start_date: '2014-09',
          end_date: '2018-05',
          location: 'Berkeley, CA',
          highlights: ['Magna Cum Laude', 'Deans List all semesters']
        }
      ],
      Experience: [
        {
          id: 'exp1',
          company: 'Tech Company',
          position: 'Senior Software Engineer',
          start_date: '2022-01',
          end_date: 'present',
          location: 'San Francisco, CA',
          highlights: [
            'Led development of microservices architecture serving 1M+ daily users',
            'Reduced API response time by 40% through optimization',
            'Mentored 3 junior developers'
          ]
        },
        {
          id: 'exp2',
          company: 'Startup Inc',
          position: 'Software Engineer',
          start_date: '2020-07',
          end_date: '2021-12',
          location: 'Remote',
          highlights: [
            'Built React dashboard used by 500+ enterprise customers',
            'Implemented CI/CD pipeline reducing deployment time by 60%'
          ]
        }
      ],
      Skills: [
        'Programming: Python, JavaScript, TypeScript, Go',
        'Frameworks: React, Next.js, Node.js, FastAPI',
        'Databases: PostgreSQL, Redis, MongoDB',
        'DevOps: AWS, Docker, Kubernetes, Git, CI/CD'
      ]
    }
  },
  design: {
    theme: 'classic'
  }
};

// Generate unique ID
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

// Create empty experience entry
export function createEmptyExperience(): ExperienceEntry {
  return {
    id: generateId(),
    company: '',
    position: '',
    location: '',
    start_date: '',
    end_date: 'present',
    highlights: ['']
  };
}

// Create empty education entry
export function createEmptyEducation(): EducationEntry {
  return {
    id: generateId(),
    institution: '',
    area: '',
    degree: '',
    start_date: '',
    end_date: '',
    location: '',
    highlights: []
  };
}

// Create empty normal entry
export function createEmptyNormalEntry(): NormalEntry {
  return {
    id: generateId(),
    name: '',
    location: '',
    date: '',
    highlights: []
  };
}

// Create empty publication entry
export function createEmptyPublicationEntry(): PublicationEntry {
  return {
    id: generateId(),
    title: '',
    authors: [],
    date: '',
    doi: '',
    journal: '',
    url: ''
  };
}

// Create empty one-line entry
export function createEmptyOneLineEntry(): OneLineEntry {
  return {
    id: generateId(),
    label: '',
    details: ''
  };
}

// Factory function to create empty entry by type
export function createEmptyEntry(type: EntryTypeId): AnyEntry {
  switch (type) {
    case 'experience':
      return createEmptyExperience();
    case 'education':
      return createEmptyEducation();
    case 'normal':
      return createEmptyNormalEntry();
    case 'publication':
      return createEmptyPublicationEntry();
    case 'oneline':
      return createEmptyOneLineEntry();
    case 'text':
    default:
      return '';
  }
}

// Convert ResumeData to YAML string for API
export function resumeToYaml(resume: ResumeData, options: { keepEmpty?: boolean } = {}): string {
  // Remove empty entries and format for YAML
  const cleanResume = JSON.parse(JSON.stringify(resume));
  const { keepEmpty = false } = options;

  // Clean experience - remove empty entries, strip IDs
  const rawExperience = cleanResume.cv.sections.Experience || cleanResume.cv.sections.experience;
  const cleanedExperience = rawExperience
    ? rawExperience
      .filter((exp: ExperienceEntry) => keepEmpty || (exp.company || exp.position))
      .map((exp: ExperienceEntry) => {
        const { id, ...rest } = exp;
        return {
          ...rest,
          highlights: rest.highlights.filter((h: string) => keepEmpty || h.trim())
        };
      })
    : [];

  // Clean education
  const rawEducation = cleanResume.cv.sections.Education || cleanResume.cv.sections.education;
  const cleanedEducation = rawEducation
    ? rawEducation
      .filter((edu: EducationEntry) => keepEmpty || edu.institution)
      .map((edu: EducationEntry) => {
        const { id, ...rest } = edu;
        return rest;
      })
    : [];

  // Clean skills - filter empty
  const rawSkills = cleanResume.cv.sections.Skills || cleanResume.cv.sections.skills;
  const cleanedSkills = rawSkills
    ? rawSkills.filter((s: string) => keepEmpty || s.trim())
    : [];

  // Clean summary
  const rawSummary = cleanResume.cv.sections.Summary || cleanResume.cv.sections.summary;
  const cleanedSummary = rawSummary
    ? rawSummary.filter((s: string) => keepEmpty || s.trim())
    : [];

  // Clean Projects (Using ExperienceEntry structure)
  const rawProjects = cleanResume.cv.sections.Projects || cleanResume.cv.sections.projects;
  const cleanedProjects = rawProjects
    ? rawProjects
      .filter((proj: ExperienceEntry) => keepEmpty || (proj.company || proj.position))
      .map((proj: ExperienceEntry) => {
        const { id, ...rest } = proj;
        return {
          ...rest,
          highlights: rest.highlights ? rest.highlights.filter((h: string) => keepEmpty || h.trim()) : []
        };
      })
    : [];

  // Clean Publications
  const rawPublications = cleanResume.cv.sections.Publications || cleanResume.cv.sections.publications;
  const cleanedPublications = rawPublications
    ? rawPublications
      .filter((pub: PublicationEntry) => keepEmpty || pub.title)
      .map((pub: PublicationEntry) => {
        const { id, ...rest } = pub;
        return rest;
      })
    : [];

  // Clean Extracurricular Activities (Using ExperienceEntry structure)
  const rawExtracurricular = cleanResume.cv.sections["Extracurricular Activities"] || cleanResume.cv.sections.extracurricular_activities;
  const cleanedExtracurricular = rawExtracurricular
    ? rawExtracurricular
      .filter((act: ExperienceEntry) => keepEmpty || (act.company || act.position))
      .map((act: ExperienceEntry) => {
        const { id, ...rest } = act;
        return {
          ...rest,
          highlights: rest.highlights ? rest.highlights.filter((h: string) => keepEmpty || h.trim()) : []
        };
      })
    : [];

  // Enforce section order based on resume.cv.section_order or default
  const orderedSections: Record<string, any> = {};

  // Default order if not specified
  const DEFAULT_ORDER = [
    'Summary',
    'Experience',
    'Projects',
    'Education',
    'Skills',
    'Publications',
    'Extracurricular Activities'
  ];

  // Combine default order with any custom sections found in the resume
  // but prioritize explicit section_order if present
  let sectionOrder = cleanResume.cv.section_order || DEFAULT_ORDER;

  // Add any sections present in data but missing from order (to be safe)
  const existingSections = Object.keys(cleanResume.cv.sections);
  for (const sect of existingSections) {
    if (!sectionOrder.includes(sect)) {
      sectionOrder.push(sect);
    }
  }

  // Build orderedSections object
  for (const sectionName of sectionOrder) {
    // Determine the source data key (handle case sensitivity mapping if needed)
    // We assume keys in cleanResume.cv.sections match sectionName or are mapped earlier
    let content: any = null;

    if (sectionName === 'Summary') content = cleanedSummary;
    else if (sectionName === 'Experience') content = cleanedExperience;
    else if (sectionName === 'Education') content = cleanedEducation;
    else if (sectionName === 'Skills') content = cleanedSkills;
    else if (sectionName === 'Projects') content = cleanedProjects;
    else if (sectionName === 'Publications') content = cleanedPublications;
    else if (sectionName === 'Extracurricular Activities') content = cleanedExtracurricular;
    else {
      // Custom sections logic
      // Note: We already have custom sections in cleanResume.cv.sections, but they haven't been "cleaned" explicitly
      // by the blocks above unless we add generic cleaning. 
      // The previous generic loop (lines 560-579) did the cleaning.
      // We should probably rely on the previously built 'cleanResume.cv.sections' for source?
      // However, the specific blocks above (cleanedSummary etc) created NEW objects.
      // So we must use those for standard sections.
      // For custom sections, we need to pick them from cleanResume.cv.sections AND clean them if not done.
      // The previous code had a generic loop at the end. I should preserve that logic but integrate it here.
      
      const rawContent = cleanResume.cv.sections[sectionName] || cleanResume.cv.sections[sectionName.toLowerCase()];
      if (Array.isArray(rawContent) && rawContent.length > 0) {
         // Generic cleaner for custom sections
         const cleanedEntries = rawContent.map((entry: any) => {
            if (typeof entry === 'string') return entry;
            if (typeof entry === 'object' && entry !== null) {
              const { id, ...rest } = entry;
              return rest;
            }
            return entry;
          }).filter((entry: any) => {
            if (keepEmpty) return true;
            if (typeof entry === 'string') return entry.trim().length > 0;
            if (typeof entry === 'object') return Object.values(entry).some(v => v && String(v).trim());
            return false;
          });
          if (keepEmpty || cleanedEntries.length > 0) {
            content = cleanedEntries;
          }
      }
    }

    if (content && (keepEmpty || (Array.isArray(content) && content.length > 0))) {
      orderedSections[sectionName] = content;
    }
  }

  cleanResume.cv.sections = orderedSections;

  // Remove section_order from output yaml (RenderCV doesn't use it, it uses key order)
  delete cleanResume.cv.section_order;

  // Remove _customSectionTypes metadata (not sent to API)
  delete cleanResume.cv._customSectionTypes;

  // Clean social networks
  cleanResume.cv.social_networks = cleanResume.cv.social_networks
    .filter((sn: SocialNetwork) => keepEmpty || sn.username.trim());

  // Helper function to remove empty nested objects/values
  const cleanObject = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;
    const cleaned: any = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      if (!keepEmpty && (value === '' || value === null || value === undefined)) continue;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedNested = cleanObject(value);
        if (keepEmpty || Object.keys(cleanedNested).length > 0) {
          cleaned[key] = cleanedNested;
        }
      } else {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  // Clean design settings - remove empty nested objects
  if (cleanResume.design) {
    cleanResume.design = cleanObject(cleanResume.design);
  }

  // Clean locale settings
  if (cleanResume.locale) {
    cleanResume.locale = cleanObject(cleanResume.locale);
    if (!keepEmpty && Object.keys(cleanResume.locale).length === 0) {
      delete cleanResume.locale;
    }
  }

  // Convert to actual YAML format (RenderCV compatible)
  return yaml.dump(cleanResume, {
    indent: 2,
    lineWidth: -1, // No line wrapping
    noRefs: true,
    sortKeys: false,
    quotingType: '"',
    forceQuotes: false
  });
}

// Parse YAML response into ResumeData (from API or editor)
export function yamlToResume(yamlString: string, options: { createDefaultOnError?: boolean } = {}): ResumeData {
  const { createDefaultOnError = true } = options;
  try {
    // Try parsing as YAML first (handles both YAML and JSON)
    const data = yaml.load(yamlString) as any;

    if (!data) {
      throw new Error('Parsed YAML is empty');
    }

    // Ensure structure matches our types, add IDs
    const sections = data.cv?.sections || {};
    const resume: ResumeData = {
      cv: {
        name: data.cv?.name || '',
        email: data.cv?.email || '',
        phone: data.cv?.phone || '',
        location: data.cv?.location || '',
        website: data.cv?.website || '',
        social_networks: data.cv?.social_networks || [],
        // Capture section order from the parsed YAML keys
        section_order: Object.keys(sections).filter(k => 
           // Filter out known non-section keys just in case, though usually sections only contains sections
           !['name', 'email', 'phone', 'location', 'website', 'social_networks'].includes(k)
        ),
        sections: {
          Summary: sections.Summary || sections.summary || [''],
          Experience: (sections.Experience || sections.experience || []).map((exp: any) => ({
            id: generateId(),
            ...exp,
            highlights: exp.highlights || ['']
          })),
          Education: (sections.Education || sections.education || []).map((edu: any) => ({
             id: generateId(),
             ...edu
          })),
          Skills: sections.Skills || sections.skills || [],
          Projects: (sections.Projects || sections.projects || []).map((proj: any) => ({
             id: generateId(),
             ...proj,
             highlights: proj.highlights || ['']
          })),
          Publications: (sections.Publications || sections.publications || []).map((pub: any) => ({
             id: generateId(),
             ...pub
          })),
          "Extracurricular Activities": (sections["Extracurricular Activities"] || sections.extracurricular_activities || []).map((act: any) => ({
             id: generateId(),
             ...act,
             highlights: act.highlights || ['']
          }))
        }
      },
      design: {
        theme: data.design?.theme || 'classic'
      }
    };

    return resume;
  } catch (error) {
    if (createDefaultOnError) {
      console.warn('Failed to parse YAML, returning default resume:', error);
      return defaultResumeData;
    }
    throw error;
  }
}
